import React, { useEffect, useState, useRef } from "react";
import {
  getActivities,
  createActivity,
  updateActivity,
  deleteActivity,
} from "../api/managerActivityService";
import { getVenues } from "../api/venueService";

const ActivitiesContent = () => {
  const [activities, setActivities] = useState([]);
  const [venues, setVenues] = useState([]); // To store venue options
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [deleteMode, setDeleteMode] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [selectedActivity, setSelectedActivity] = useState(null); // For edit/delete
  const [form, setForm] = useState({
    title: "",
    description: "",
    type: "",
    startTime: "",
    endTime: "",
    location: "",
    venueId: "",
  });
  const [confirmTitle, setConfirmTitle] = useState("");
  const isMounted = useRef(false);

  const fetchActivities = async (limit, lastEvaluatedKey) => {
    try {
      const response = await getActivities(limit, lastEvaluatedKey);
      setActivities((prevActivities) => [...prevActivities, ...response.activities]);
      setPagination(response.pagination);
    } catch (err) {
      setError("Failed to fetch activities. Please try again later.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const fetchVenues = async () => {
    try {
      const response = await getVenues(100, null); // Fetch up to 100 venues for the dropdown
      setVenues(response.venues);
    } catch (err) {
      setError("Failed to fetch venues. Please try again later.");
    }
  };

  useEffect(() => {
    if (!isMounted.current) {
      fetchActivities(10, null);
      fetchVenues();
      isMounted.current = true;
    }
  }, []);

  const handleLoadMore = () => {
    if (pagination.lastEvaluatedKey) {
      setLoadingMore(true);
      fetchActivities(10, pagination.lastEvaluatedKey);
    }
  };

  const handleOpenModal = (activity = null, isDelete = false) => {
    setSelectedActivity(activity);

    setForm(
      activity
        ? {
          ...activity,
          startTime: activity.startTime ? new Date(activity.startTime).toISOString().slice(0, 16) : "",
          endTime: activity.endTime ? new Date(activity.endTime).toISOString().slice(0, 16) : "",
        }
        : {
          title: "",
          description: "",
          type: "",
          startTime: "",
          endTime: "",
          location: "",
          venueId: "",
        }
    );

    setDeleteMode(isDelete);
    setShowModal(true);
    setConfirmTitle("");
    setSuccessMessage("");
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedActivity(null);
    setDeleteMode(false);
    setConfirmTitle("");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleConfirmTitleChange = (e) => {
    setConfirmTitle(e.target.value);
  };

  const handleSave = async () => {
    if (!form.venueId) {
      alert("Please select a venue.");
      return;
    }

    try {
      if (selectedActivity) {
        await updateActivity(selectedActivity.activityId, form);
        setActivities((prevActivities) =>
          prevActivities.map((activity) =>
            activity.activityId === selectedActivity.activityId
              ? { ...activity, ...form }
              : activity
          )
        );
      } else {
        const newActivity = await createActivity(form);
        setActivities((prevActivities) => [newActivity, ...prevActivities]);
        setSuccessMessage("Activity created successfully.");
      }
      handleCloseModal();
      setTimeout(() => setSuccessMessage(""), 5000);
    } catch (err) {
      alert("Failed to save activity. Please try again.");
    }
  };

  const handleDelete = async () => {
    if (confirmTitle !== selectedActivity.title) {
      alert("Entered title does not match. Please try again.");
      return;
    }

    try {
      await deleteActivity(selectedActivity.activityId);
      setActivities((prevActivities) =>
        prevActivities.filter((activity) => activity.activityId !== selectedActivity.activityId)
      );
      setSuccessMessage("Activity deleted successfully.");
      handleCloseModal();
      setTimeout(() => setSuccessMessage(""), 5000);
    } catch (err) {
      alert("Failed to delete activity. Please try again.");
    }
  };

  return (
    <div className="activities-content">
      <h2 className="text-2xl font-bold mb-4">Activities</h2>
      <p>View detailed information for activities.</p>

      {/* Success Message */}
      {successMessage && <div className="alert alert-success">{successMessage}</div>}

      {!loading && (
        <button className="btn btn-success mb-3" onClick={() => handleOpenModal()}>
          Create New Activity
        </button>
      )}

      {loading ? (
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      ) : error ? (
        <div className="alert alert-danger">{error}</div>
      ) : (
        <div>
          <h3>List of Activities</h3>
          {activities.length > 0 ? (
            <table className="table table-striped">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Title</th>
                  <th>Description</th>
                  <th>Type</th>
                  <th>Start Time</th>
                  <th>End Time</th>
                  <th>Location</th>
                  <th>Venue</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {activities.map((activity, index) => (
                  <tr
                    key={activity.activityId}
                    style={{
                      backgroundColor: activity.isApproved === "true" ? "#d4edda" : "#fff3cd", // Custom colors
                      "--bs-table-bg": "transparent", // Override Bootstrap variable
                    }}
                  >
                    <td>{index + 1}</td>
                    <td>{activity.title}</td>
                    <td>{activity.description}</td>
                    <td>{activity.type}</td>
                    <td>{new Date(activity.startTime).toLocaleString()}</td>
                    <td>{new Date(activity.endTime).toLocaleString()}</td>
                    <td>{activity.location}</td>
                    <td>{venues.find((venue) => venue.venueId === activity.venueId)?.name || "N/A"}</td>
                    <td>
                      <button
                        className="btn btn-warning btn-sm me-2"
                        onClick={() => handleOpenModal(activity)}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleOpenModal(activity, true)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p>No activities found.</p>
          )}
          {pagination.lastEvaluatedKey && (
            <button
              className="btn btn-primary mt-3"
              onClick={handleLoadMore}
              disabled={loadingMore}
            >
              {loadingMore ? "Loading..." : "Load More"}
            </button>
          )}
        </div>
      )}

      {showModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {deleteMode
                    ? "Confirm Activity Deletion"
                    : selectedActivity
                      ? "Edit Activity"
                      : "Create New Activity"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseModal}
                ></button>
              </div>
              <div className="modal-body">
                {deleteMode ? (
                  <div>
                    <p>
                      Are you sure you want to delete the activity{" "}
                      <strong>{selectedActivity.title}</strong>? Please confirm its title.
                    </p>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter activity's title"
                      value={confirmTitle}
                      onChange={handleConfirmTitleChange}
                    />
                  </div>
                ) : (
                  <form>
                    <div className="mb-3">
                      <label className="form-label">Title</label>
                      <input
                        type="text"
                        className="form-control"
                        name="title"
                        value={form.title}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Description</label>
                      <textarea
                        className="form-control"
                        name="description"
                        value={form.description}
                        onChange={handleChange}
                      ></textarea>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Type</label>
                      <input
                        type="text"
                        className="form-control"
                        name="type"
                        value={form.type}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Start Time</label>
                      <input
                        type="datetime-local"
                        className="form-control"
                        name="startTime"
                        value={form.startTime}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">End Time</label>
                      <input
                        type="datetime-local"
                        className="form-control"
                        name="endTime"
                        value={form.endTime}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Location</label>
                      <input
                        type="text"
                        className="form-control"
                        name="location"
                        value={form.location}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Venue</label>
                      <select
                        className="form-control"
                        name="venueId"
                        value={form.venueId}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select Venue</option>
                        {venues.map((venue) => (
                          <option key={venue.venueId} value={venue.venueId}>
                            {venue.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </form>
                )}
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={handleCloseModal}
                >
                  Cancel
                </button>
                {deleteMode ? (
                  <button
                    className="btn btn-danger"
                    onClick={handleDelete}
                  >
                    Confirm Delete
                  </button>
                ) : (
                  <button
                    className="btn btn-primary"
                    onClick={handleSave}
                  >
                    Save
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivitiesContent;
